import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { baseCase, combinedOpportunity, illustrativeNote, marketPaths, sourcesNote } from '../../data/deckSlide9';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4;

/** Column geometry on the fixed 1600×900 canvas. */
const COLUMNS = {
  app: {
    x: 690,
    width: 330
  },
  business: {
    x: 1108,
    width: 380
  }
};
const HEAD_Y = 296;
const PATH_TOP = 616;
const PATH_GAP = 52;
export function MarketSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(650, 1); // engine + the two markets
    at(2100, 2); // the illustrative opportunity
    at(3500, 3); // expansion paths
    at(4700, 4); // the base case payoff
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2408" data-mp="3q6qivzgxsf15">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2409" data-mp="3ldfl0nu3r8ld">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 6% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2410" data-mp="3lbb67auo0p6b" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2411" data-mp="32ejov6yook4w">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2412" data-mp="10mwvwzv8ef5s" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2413" data-mp="wllg1m25t8as">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2414" data-mp="2q39a8brffuzb">
          10 · Market Opportunity
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 128,
        width: 500
      }} data-id="element-2415" data-mp="38w4pu1wscwh5">
          {['One platform.', 'Two massive markets.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2416" data-mp="34tggzu82npk6" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 238,
        width: 452
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2417" data-mp="1kw9efdlbbku0">
          Starting with professionals in the U.S. — expanding through the same
          AI platform into businesses and international markets.
        </motion.p>

        {/* LEFT — the combined illustrative opportunity */}
        <motion.div className="absolute" style={{
        left: 112,
        top: 350,
        width: 470
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2418" data-mp="x9194vnvwbrz">
          <p className="text-[54px] font-semibold leading-none tracking-[-0.03em] text-[#0B1712] tabular-nums" data-id="element-2419" data-mp="jfjqdd30v330">
            {combinedOpportunity.total}
          </p>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-vexu-700" data-id="element-2420" data-mp="efe3sbbm06rs">
            {combinedOpportunity.label}
          </p>
          <div className="mt-4 flex items-baseline gap-3 text-[13.5px] text-[#5A6B60]" data-id="element-2421" data-mp="3iv41505ngme6">
            <span className="tabular-nums" data-id="element-2422" data-mp="3muuzboxia4sq">
              <span className="font-semibold text-[#33463B]" data-id="element-2423" data-mp="3mttbskzazpu4">
                {combinedOpportunity.parts[0].value}
              </span>{' '}
              {combinedOpportunity.parts[0].label}
            </span>
            <span className="text-[#A3AFA7]" data-id="element-2424" data-mp="ipbpow6hfo0">+</span>
            <span className="tabular-nums" data-id="element-2425" data-mp="5e4qcj20fflh">
              <span className="font-semibold text-[#33463B]" data-id="element-2426" data-mp="5h9344tjjjnr">
                {combinedOpportunity.parts[1].value}
              </span>{' '}
              {combinedOpportunity.parts[1].label}
            </span>
          </div>
        </motion.div>

        {/* LEFT — the base case against that opportunity */}
        <motion.div className="absolute border-t border-[#0B1712]/[0.09] pt-7" style={{
        left: 112,
        top: 520,
        width: 470
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(4) ? 1 : 0,
        y: shown(4) ? 0 : 10
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-2427" data-mp="2g7hdu4un4zml">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A998F]" data-id="element-2428" data-mp="1yxn68padhyf5">
            Base case · Year 3
          </p>
          <p className="mt-3 text-[17px] text-[#33463B]" data-id="element-2429" data-mp="1uy4m3vqdlrdd">
            <span className="text-[21px] font-bold tracking-[-0.015em] text-[#0B1712] tabular-nums" data-id="element-2430" data-mp="2rnc8f5ytk4ic">
              {baseCase.revenue}
            </span>{' '}
            Year-3 revenue represents only
          </p>
          <p className="mt-2 text-[62px] font-semibold leading-none tracking-[-0.03em] text-vexu-700 tabular-nums" data-id="element-2431" data-mp="2ilcpu985osda">
            {baseCase.share}
          </p>
          <p className="mt-3 text-[14.5px] text-[#5A6B60]" data-id="element-2432" data-mp="2dpdtnzgl662b">
            of the illustrative U.S. opportunity
          </p>

          <div className="mt-6 flex gap-10" data-id="element-2433" data-mp="127ctt9cflmvb">
            {[{
            group: 'Consumer',
            value: '18,577 billed subscribers',
            share: '~0.20% of focused U.S. beachhead'
          }, {
            group: 'Business',
            value: '1,441 active customers',
            share: '~0.023% of U.S. employer-firm market'
          }].map(item => <div key={item.group} className="max-w-[215px]" data-id="element-2434" data-mp="yo5ccxjuz38c">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A3AFA7]" data-id="element-2435" data-mp="kyab6lr99l56">
                  {item.group}
                </p>
                <p className="mt-1.5 text-[13px] leading-snug text-[#33463B] tabular-nums" data-id="element-2436" data-mp="pr3dl2z94he3">
                  {item.value}
                </p>
                <p className="mt-0.5 text-[12px] leading-snug text-[#8A998F]" data-id="element-2437" data-mp="fypb280cn6qv">
                  {item.share}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Shared engine */}
        <motion.div className="absolute text-center" style={{
        left: 1020,
        top: 128,
        width: 140
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2438" data-mp="6y9952z987o4">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-full" style={{
          backgroundColor: 'rgba(0,200,83,0.10)'
        }} data-id="element-2439" data-mp="3sjum9a29d74m">
            <img src={VEXU_LOGO} alt="" className="h-6 w-6 object-contain" data-id="element-2440" data-mp="6bbgmhx3qam8" />
          </div>
          <p className="mt-3 text-[13.5px] font-semibold tracking-[-0.01em] text-[#0B1712]" data-id="element-2441" data-mp="1da126yvwtfi">
            VEXU AI
          </p>
        </motion.div>

        {/* Arcs from the engine into both markets */}
        <svg className="absolute" style={{
        left: 640,
        top: 210,
        width: 880,
        height: 86
      }} viewBox="0 0 880 86" fill="none" aria-hidden="true" data-id="element-2442" data-mp="b21pi54qkvlo">
          <motion.path d="M450 4 C450 46, 52 34, 52 82" stroke="rgba(0,200,83,0.4)" strokeWidth="1" initial={{
          pathLength: 0
        }} animate={{
          pathLength: shown(1) ? 1 : 0
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease
        }} data-id="element-2443" data-mp="k3rnxux1cusy" />
          <motion.path d="M450 4 C450 46, 470 34, 470 82" stroke="rgba(0,200,83,0.4)" strokeWidth="1" initial={{
          pathLength: 0
        }} animate={{
          pathLength: shown(1) ? 1 : 0
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease
        }} data-id="element-2444" data-mp="20g5a9uzd4r0u" />
        </svg>

        {/* The two markets */}
        {marketPaths.map((path, columnIndex) => {
        const col = COLUMNS[path.id];
        const delay = columnIndex * 0.16;
        return <div key={path.id} data-id="element-2445" data-mp="1mgvsiqvidour" data-mpi={columnIndex}>
              <motion.div className="absolute" style={{
            left: col.x,
            top: HEAD_Y,
            width: col.width
          }} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: shown(1) ? 1 : 0,
            y: shown(1) ? 0 : 10
          }} transition={{
            duration: 0.8,
            delay: 0.45 + delay,
            ease
          }} data-id="element-2446" data-mp="1mhx6p47tmrgp">
                <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0B1712]" data-id="element-2447" data-mp="hh7epclffxc1">
                  {path.name}
                </p>
                <p className="mt-1.5 text-[12.5px] text-[#7C8B82]" data-id="element-2448" data-mp="cgt5hr83ahcf">
                  {path.audience} · {path.monetisation}
                </p>
                {path.note && <p className="mt-1 text-[11.5px] text-[#A3AFA7]" data-id="element-2449" data-mp="3ipsjjb66sw8">
                    {path.note}
                  </p>}

                <p className="mt-6 text-[34px] font-semibold leading-none tracking-[-0.025em] text-[#0B1712] tabular-nums" data-id="element-2450" data-mp="3uusj8ada8as0">
                  {path.market.value}
                </p>
                <p className="mt-2 text-[13.5px] text-[#33463B]" data-id="element-2451" data-mp="3l08yiyftot8f">
                  {path.market.unit}
                </p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-vexu-700" data-id="element-2452" data-mp="3bxrdgnbwm6n0">
                  {path.market.label}
                </p>
                <p className="mt-1 text-[10.5px] text-[#A3AFA7]" data-id="element-2453" data-mp="bh0e5c5rwt1z">
                  {path.market.source}
                </p>
              </motion.div>

              <motion.div className="absolute" style={{
            left: col.x,
            top: path.id === 'business' ? 512 : 482,
            width: col.width
          }} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: shown(2) ? 1 : 0,
            y: shown(2) ? 0 : 8
          }} transition={{
            duration: 0.8,
            delay: delay,
            ease
          }} data-id="element-2454" data-mp="l84p80sbvtrj">
                <p className="text-[26px] font-semibold leading-none tracking-[-0.02em] text-vexu-700 tabular-nums" data-id="element-2455" data-mp="2jho1td9voj8">
                  {path.opportunity.value}
                </p>
                <p className="mt-2 text-[12.5px] text-[#5A6B60]" data-id="element-2456" data-mp="1dw0mp7pq1ppd">
                  {path.opportunity.label}
                </p>
                <p className="mt-1 text-[10.5px] text-[#A3AFA7]" data-id="element-2457" data-mp="18xpcuwzfff75">
                  {path.opportunity.basis}
                </p>
              </motion.div>

              {/* expansion path — deliberately secondary */}
              <motion.div className="absolute w-px" style={{
            left: col.x,
            top: PATH_TOP - 6,
            backgroundColor: 'rgba(0,200,83,0.14)'
          }} initial={{
            height: 0
          }} animate={{
            height: shown(3) ? (path.stages.length - 1) * PATH_GAP + 6 : 0
          }} transition={{
            duration: 0.9,
            ease
          }} data-id="element-2458" data-mp="1le87rus7b5er" />
              {path.stages.map((item, i) => <motion.div key={item.label} className="absolute" style={{
            left: col.x + 16,
            top: PATH_TOP + i * PATH_GAP,
            width: col.width - 16
          }} initial={{
            opacity: 0,
            x: -5
          }} animate={{
            opacity: shown(3) ? 1 : 0,
            x: shown(3) ? 0 : -5
          }} transition={{
            duration: 0.6,
            delay: 0.15 + i * 0.16,
            ease
          }} data-id="element-2459" data-mp="1ufy9btfsrkq9" data-mpi={i}>
                  <span className="absolute rounded-full" style={{
              left: -19,
              top: 6,
              width: 5,
              height: 5,
              backgroundColor: i === 0 ? 'rgba(0,153,64,0.6)' : 'rgba(0,153,64,0.2)'
            }} data-id="element-2460" data-mp="2iuautgnu6mqq" />
                  <p className={`text-[12.5px] leading-tight ${i === 0 ? 'font-medium text-[#7C8B82]' : 'text-[#94A29A]'}`} data-id="element-2461" data-mp="203tg4fzu9rod">
                    {item.label}
                  </p>
                  {item.detail && <p className="mt-0.5 text-[10.5px] leading-snug text-[#B4BEB8]" data-id="element-2462" data-mp="1q5vp5kqlhgs2">
                      {item.detail}
                    </p>}
                </motion.div>)}
            </div>;
      })}

        {/* Footnotes */}
        <motion.div className="absolute" style={{
        left: 112,
        right: 112,
        bottom: 40
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.4,
        ease
      }} data-id="element-2463" data-mp="1q0o57prjjod9">
          <p className="text-[11px] leading-snug text-[#8A998F]" data-id="element-2464" data-mp="129io779v6q1d">
            {illustrativeNote}
          </p>
          <p className="mt-1.5 text-[10.5px] leading-snug text-[#A3AFA7]" data-id="element-2465" data-mp="178brk6vft0nt">
            {sourcesNote}
          </p>
        </motion.div>
      </div>
    </SlideStage>;
}